import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, ArrowLeft, Upload, Eye, Trash2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { SpreadsheetViewer } from '../components/spreadsheet/SpreadsheetViewer';
import { formatCellValue } from '../components/spreadsheet/SpreadsheetFormatter';

interface SpreadsheetFile {
  id: string;
  name: string;
  created_at: string;
  file_url: string;
}

interface SpreadsheetCell {
  value: string | number | null;
  className?: string;
}

export function FinanceXLS() {
  const [files, setFiles] = useState<SpreadsheetFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SpreadsheetFile | null>(null);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetCell[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [viewerLoading, setViewerLoading] = useState(false);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('spreadsheet_files')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setFiles(data || []);
    } catch (err) {
      console.error('Error loading files:', err);
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !uploadName.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('spreadsheets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Save file metadata
      const { error: saveError } = await supabase
        .from('spreadsheet_files')
        .insert({
          name: uploadName,
          file_url: fileName // Store just the filename
        });

      if (saveError) throw saveError;

      // Refresh file list
      await loadFiles();
      setUploadName('');
      setShowUploadForm(false);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const getSignedUrl = async (fileName: string): Promise<string> => {
    const { data, error } = await supabase.storage
      .from('spreadsheets')
      .createSignedUrl(fileName, 60 * 60); // 1 hour expiry

    if (error) throw error;
    if (!data.signedUrl) throw new Error('Failed to get signed URL');
    return data.signedUrl;
  };

  const handleDelete = async (file: SpreadsheetFile) => {
    try {
      // Delete from storage
      await supabase.storage
        .from('spreadsheets')
        .remove([file.file_url]);

      // Delete metadata
      await supabase
        .from('spreadsheet_files')
        .delete()
        .eq('id', file.id);

      await loadFiles();
      if (selectedFile?.id === file.id) {
        setSelectedFile(null);
        setSpreadsheetData([]);
        setHeaders([]);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  const handleDownload = async (file: SpreadsheetFile) => {
    try {
      const signedUrl = await getSignedUrl(file.file_url);
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const extension = file.file_url.split('.').pop()?.toLowerCase() || 'xlsx';
      link.download = `${file.name}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to download file');
    }
  };

  const viewFile = async (file: SpreadsheetFile) => {
    try {
      setViewerLoading(true);
      setError(null);
      
      const signedUrl = await getSignedUrl(file.file_url);
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          if (!data) throw new Error('Failed to read file data');
          
          const workbook = XLSX.read(data, { type: 'array', cellStyles: true });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          
          const range = XLSX.utils.decode_range(firstSheet['!ref'] || 'A1');
          const headers: string[] = [];
          const formattedData: SpreadsheetCell[][] = [];

          // Extract headers
          for (let C = range.s.c; C <= range.e.c; C++) {
            const headerCell = firstSheet[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
            headers.push(headerCell?.v || '');
          }

          // Extract and format data
          for (let R = range.s.r + 1; R <= range.e.r; R++) {
            const row: SpreadsheetCell[] = [];
            for (let C = range.s.c; C <= range.e.c; C++) {
              const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
              const cell = firstSheet[cellRef];
              const format = firstSheet[cellRef]?.z ? { numFmt: firstSheet[cellRef].z } : undefined;
              
              row.push(formatCellValue(cell?.v, format));
            }
            formattedData.push(row);
          }

          setHeaders(headers);
          setSpreadsheetData(formattedData);
          setSelectedFile(file);
        } catch (err) {
          console.error('Error processing spreadsheet:', err);
          setError(err instanceof Error ? err.message : 'Failed to process spreadsheet');
        } finally {
          setViewerLoading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setViewerLoading(false);
      };

      reader.readAsArrayBuffer(blob);
    } catch (err) {
      console.error('Error viewing file:', err);
      setError(err instanceof Error ? err.message : 'Failed to view file');
      setViewerLoading(false);
    }
  };

  if (selectedFile) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center mb-6">
            <FileSpreadsheet className="w-8 h-8 text-amber-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Spreadsheet Viewer</h1>
          </div>

          <SpreadsheetViewer
            name={selectedFile.name}
            headers={headers}
            data={spreadsheetData}
            loading={viewerLoading}
            onClose={() => setSelectedFile(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FileSpreadsheet className="w-8 h-8 text-amber-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Spreadsheet Management</h1>
          </div>
          <div className="flex items-center space-x-4">
            {!showUploadForm && (
              <button
                onClick={() => setShowUploadForm(true)}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Spreadsheet
              </button>
            )}
            <Link
              to="/finance"
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Finance
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {showUploadForm && (
          <div className="mb-6 bg-white rounded-lg shadow-lg p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500"
                placeholder="Enter file name"
              />
            </div>
            <div className="flex items-center justify-between">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
              />
              <button
                onClick={() => setShowUploadForm(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading...</p>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center bg-white rounded-lg shadow-lg p-12">
            <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No spreadsheets uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {files.map((file) => (
              <div
                key={file.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">{file.name}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDownload(file)}
                        className="p-1 text-gray-500 hover:text-amber-600"
                        title="Download"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => viewFile(file)}
                        className="p-1 text-gray-500 hover:text-amber-600"
                        title="View"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(file)}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className="px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => viewFile(file)}
                >
                  <div className="flex items-center text-sm text-gray-600">
                    <Eye className="w-4 h-4 mr-2" />
                    View Spreadsheet
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}